import React from 'react';
import { Link } from 'react-router-dom';
import { Globe, MapPin, ArrowRight, X, RefreshCw, Check } from 'lucide-react';
import Button from '../shared/Button';
import useVendorLocationAlert from '../../hooks/useVendorLocationAlert';

const VendorLocationAlertBanner = ({ compact = false, showIfDismissed = false }) => {
  const {
    isMismatch,
    isDismissed,
    savedLocation,
    detectedLocation,
    isDetecting,
    isUpdating,
    dismissAlert,
    updateToDetectedLocation,
    refreshDetection,
  } = useVendorLocationAlert();

  if (!isMismatch) return null;
  if (isDismissed && !showIfDismissed) return null;

  const detectedText = detectedLocation.display || detectedLocation.country;
  const savedText = savedLocation.display || savedLocation.country || 'Registered location';

  if (compact) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 sm:p-4 text-amber-900 mb-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-2.5 min-w-0">
            <Globe className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-xs sm:text-sm">
              <span className="font-semibold text-amber-950">Location difference detected: </span>
              You are connecting from <span className="font-medium text-amber-900 underline decoration-amber-300">{detectedText}</span>, while your business is listed in <span className="font-medium text-amber-900">{savedText}</span>.
            </div>
          </div>
          <button
            type="button"
            onClick={dismissAlert}
            className="text-amber-500 hover:text-amber-700 p-1 rounded-md"
            title="Dismiss notification"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="mt-2.5 flex flex-wrap items-center gap-2 pl-7">
          <Button
            size="xs"
            onClick={updateToDetectedLocation}
            disabled={isUpdating}
            className="bg-amber-600 hover:bg-amber-700 text-white border-transparent"
          >
            {isUpdating ? (
              <>
                <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                Updating...
              </>
            ) : (
              <>
                <Check className="w-3 h-3 mr-1" />
                Update to {detectedLocation.city || detectedLocation.country}
              </>
            )}
          </Button>
          <button
            type="button"
            onClick={dismissAlert}
            className="text-xs font-medium text-amber-800 hover:text-amber-950 px-2 py-1 rounded hover:bg-amber-100 transition-colors"
          >
            Keep {savedText}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 border-y sm:border sm:rounded-xl border-amber-200/80 shadow-sm p-4 sm:p-5 mb-6 text-amber-950">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-start gap-3.5 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-amber-100 border border-amber-200 flex items-center justify-center flex-shrink-0 text-amber-700">
            <Globe className="w-5 h-5 animate-pulse" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-200/70 text-amber-900 border border-amber-300/50">
                Connection Notice
              </span>
              <h3 className="text-sm sm:text-base font-semibold text-amber-950">
                New location detected ({detectedText})
              </h3>
            </div>
            <p className="mt-1 text-xs sm:text-sm text-amber-800/90 leading-relaxed">
              You are currently browsing from <strong className="text-amber-950">{detectedText}</strong>. Your business operating location is registered as <strong className="text-amber-950">{savedText}</strong>. Would you like to switch your business operating location to your current location?
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 sm:gap-3 md:flex-shrink-0 self-start md:self-center pl-13 md:pl-0">
          <Button
            size="sm"
            onClick={updateToDetectedLocation}
            disabled={isUpdating}
            className="bg-amber-600 hover:bg-amber-700 text-white border-transparent shadow-sm text-xs sm:text-sm font-medium"
          >
            {isUpdating ? (
              <>
                <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                Updating...
              </>
            ) : (
              <>
                <MapPin className="w-3.5 h-3.5 mr-1.5" />
                Switch to {detectedLocation.city || detectedLocation.country}
              </>
            )}
          </Button>

          <Button
            size="sm"
            variant="outline"
            onClick={dismissAlert}
            className="border-amber-300 text-amber-900 hover:bg-amber-100/60 text-xs sm:text-sm"
          >
            Keep {savedText}
          </Button>

          <Link
            to="/vendor/account?tab=business"
            className="inline-flex items-center text-xs sm:text-sm font-medium text-amber-800 hover:text-amber-950 px-2 py-1.5 rounded hover:underline"
          >
            Manage in Account
            <ArrowRight className="w-3.5 h-3.5 ml-1" />
          </Link>

          <button
            type="button"
            onClick={dismissAlert}
            className="text-amber-500 hover:text-amber-800 p-1.5 rounded-lg hover:bg-amber-100/50 transition-colors ml-auto sm:ml-0"
            title="Dismiss notice"
            aria-label="Dismiss notice"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default VendorLocationAlertBanner;
